import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import {
  AccessEvent,
  AccessEventsFilter,
  Query,
  QueryGetAccessEventsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/v2/queries';
import { useEffect, useRef } from 'react';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';
import { DEFAULT_ACCESS_EVENT_ACTIONS, getAccessEventsDateRange } from 'v2/utils/accessEvents';

export const PAGE_SIZE = 100;

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ACCESS_EVENTS_FOR_TENANT_TYPED: TypedDocumentNode<
  Pick<Query, 'getAccessEventsForTenant'>,
  QueryGetAccessEventsForTenantArgs
> = GET_ACCESS_EVENTS_FOR_TENANT;

interface Props extends TranslatedHookProps {
  page: number;
  filter: AccessEventsFilter | null;
}

const NO_EVENTS: AccessEvent[] = [];

const useAccessEvents = ({ handleFetchError, translation, page, filter }: Props) => {
  const pageTokenMapRef = useRef<Record<number, string | null>>({ 0: null });
  const filterRef = useRef(filter);

  const [getAccessEvents, { loading, called, data, previousData, error }] = useLazyQuery(
    GET_ACCESS_EVENTS_FOR_TENANT_TYPED
  );

  const buildVariables = (token?: string | null) => ({
    nextToken: token ?? undefined,
    filter: {
      ...filter,
      actions: filter?.actions ?? [...DEFAULT_ACCESS_EVENT_ACTIONS],
      dateRange: filter?.dateRange ? getAccessEventsDateRange(filter.dateRange) : undefined
    }
  });

  useEffect(() => {
    if (filterRef.current !== filter) {
      filterRef.current = filter;
      pageTokenMapRef.current = { 0: null };
    }

    // Captured so a late response stores its token for the page/filter it was requested for,
    // not for whatever page or filter is selected when it arrives.
    const tokenMap = pageTokenMapRef.current;
    const token = tokenMap[page];
    if (token === undefined) return;

    (async () => {
      let result;
      try {
        ({ data: result } = await getAccessEvents({ variables: buildVariables(token) }));
      } catch {
        handleFetchError(translation.access_events_error_fetching);
        return;
      }
      const nextToken = result?.getAccessEventsForTenant.nextToken;
      if (nextToken) {
        tokenMap[page + 1] = nextToken;
      }
    })();
  }, [filter, page]);

  // Rows and the next-page flag are read straight from Apollo's result, so they always arrive in the same render
  // as `loading: false`. `previousData` keeps the last successful page visible while a new request is in flight
  // or after it failed.
  const { items, nextToken } = (data ?? previousData)?.getAccessEventsForTenant ?? {};
  const accessEvents = items ?? NO_EVENTS;
  const hasNextPage = !error && Boolean(nextToken);
  const rowCount = hasNextPage ? -1 : page * PAGE_SIZE + accessEvents.length;

  return {
    accessEvents,
    rowCount,
    // A lazy query reports `loading: false` until it has been called, which would flash the "no events" state.
    loading: loading || !called
  };
};

export default useAccessEvents;
